import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import "@/App.css";

import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ThemeProvider } from "@/context/ThemeProvider";
import { DensityProvider } from "@/context/DensityContext";
import { CurrencyProvider } from "@/context/CurrencyContext";

import AppLayout from "@/components/AppLayout";
import RouteGuard from "@/components/RouteGuard";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import RecoverWithCodes from "@/pages/RecoverWithCodes";
import AuthCallback from "@/pages/AuthCallback";
import Dashboard from "@/pages/Dashboard";
import Catalog from "@/pages/Catalog";
import Leaderboard from "@/pages/Leaderboard";
import CopyTrading from "@/pages/CopyTrading";
import Community from "@/pages/Community";
import Subscription from "@/pages/Subscription";
import Settings from "@/pages/Settings";
import PlaceATrade from "@/pages/PlaceATrade";
import WhosOnTop from "@/pages/WhosOnTop";
import InstrumentInspector from "@/pages/InstrumentInspector";
import TraderProfile from "@/pages/TraderProfile";
import { RBACCoPilotGate } from "@/components/RBACCoPilotGate";
import Admin from "@/pages/Admin";
import AdminRevenue from "@/pages/AdminRevenue";
import AdminInbox from "@/pages/AdminInbox";
import AdminChats from "@/pages/AdminChats";
import AdminFaqGaps from "@/pages/AdminFaqGaps";
import AdminLayer11Audit from "@/pages/AdminLayer11Audit";
import AdminSymbols from "@/pages/AdminSymbols";
import DualKeyPeerPicker from "@/pages/DualKeyPeerPicker";
import AgencyDashboard from "@/pages/AgencyDashboard";
import BrokerSignup from "@/pages/BrokerSignup";
import ApiApplicationPage from "@/pages/ApiApplicationPage";
import { useLocation } from "react-router-dom";
import AiAssistant from "@/pages/AiAssistant";
// Deprecated modules (Feb 13 2026) — kept as imports for git-restore
// convenience but their routes now render <FeatureRetired /> until the
// corresponding FEATURE_* flag is turned back on in backend/.env.
// import OptionsChain from "@/pages/OptionsChain";
// import ShortSelling from "@/pages/ShortSelling";
// import Bonds from "@/pages/Bonds";
import FeatureRetired from "@/components/FeatureRetired";
import AgencyInviteAccept from "@/pages/AgencyInviteAccept";
import About from "@/pages/About";
import Contact from "@/pages/Contact";
import FAQ from "@/pages/FAQ";
import Watch from "@/pages/Watch";
import BacktestReport from "@/pages/BacktestReport";
import PortfolioSuggestion from "@/pages/PortfolioSuggestion";
import AutopilotAuditLog from "@/pages/AutopilotAuditLog";
import ObondoBillingPage from "@/pages/ObondoBillingPage";
import ObondoCoach from "@/pages/ObondoCoach";
import LegalPage from "@/pages/LegalPage";
// Deprecated Feb 13 2026 — see FeatureRetired above.
// import ExpertCharts from "@/pages/ExpertCharts";
import AdminSettings from "@/pages/AdminSettings";
import InvestmentStrategyOnboarding from "@/pages/InvestmentStrategyOnboarding";
import PsychometricOnboarding from "@/pages/PsychometricOnboarding";
import MacroDashboard from "@/pages/MacroDashboard";
import CountryDetailPage from "@/pages/CountryDetailPage";
import AdminRiskProfiles from "@/pages/AdminRiskProfiles";
import AdminQuestionnaireI18n from "@/pages/AdminQuestionnaireI18n";
import AdminPaybill from "@/pages/AdminPaybill";
import ConsentRenewalModal from "@/components/ConsentRenewalModal";
import CookieBanner from "@/components/CookieBanner";
import CookiePreferencesLauncher from "@/components/CookiePreferencesLauncher";
import GlobalBackButton from "@/components/GlobalBackButton";
import FeedbackWidget from "@/components/FeedbackWidget";
import { useEffect, useState } from "react";
import useVersionGuard from "@/lib/useVersionGuard";
import useBehavioralTelemetry from "@/hooks/useBehavioralTelemetry";
import useRouteActivityLogger from "@/hooks/useRouteActivityLogger";
import TelemetryConsentModal from "@/components/dashboard/TelemetryConsentModal";
import PostTradeDebriefModal from "@/components/dashboard/PostTradeDebriefModal";

function SWRegister() {
    useEffect(() => {
        if ("serviceWorker" in navigator && process.env.NODE_ENV === "production") {
            // updateViaCache: 'none' tells the browser to bypass the HTTP
            // cache when checking service-worker.js for updates. Combined
            // with an explicit reg.update() on mount, this guarantees the
            // browser sees new SW versions immediately after a deploy.
            navigator.serviceWorker
                .register("/service-worker.js", { updateViaCache: "none" })
                .then((reg) => reg.update().catch(() => {}))
                .catch(() => {});
        }
    }, []);
    return null;
}

function VersionGuard() {
    useVersionGuard();
    return null;
}

function BehavioralTelemetry() {
    // Only enable telemetry for authenticated users who have consented under
    // GDPR/DPA (TelemetryConsentModal writes user.telemetry_consent = true).
    const { user } = useAuth();
    const isAuthed = Boolean(user && typeof user === "object");
    const consented = Boolean(user?.telemetry_consent);
    useBehavioralTelemetry(isAuthed && consented);
    return null;
}

function RouteActivityLogger() {
    useRouteActivityLogger();
    return null;
}


function RtlDirection() {
    // Toggle `dir="rtl"` on <html> when the user's preferred language is
    // Arabic. Falls back to LTR for every other language. Fires on user
    // load AND on preference change (via refresh in PreferencesCard).
    const { user } = useAuth();
    useEffect(() => {
        if (typeof document === "undefined") return;
        const html = document.documentElement;
        const lang = user?.preferred_language || "en";
        if (lang === "ar") {
            html.setAttribute("dir", "rtl");
            html.setAttribute("lang", "ar");
        } else {
            html.setAttribute("dir", "ltr");
            html.setAttribute("lang", lang);
        }
    }, [user?.preferred_language]);
    return null;
}

function ConsentCoordinator() {
    // Single hydration-settled gate for BOTH consent modals — neither one
    // is even mounted until AuthContext has resolved past its initial
    // `null` (loading) / `false` (guest) states to a real authenticated
    // user object with a stable `id`. Previously each modal watched raw
    // `user` independently and both re-ran their own effects on every
    // hydration tick (login -> /auth/me -> locale POST -> /auth/me again),
    // which is what produced the flicker on new-user signup.
    const { user } = useAuth();
    const [stage, setStage] = useState("legal"); // 'legal' -> 'telemetry' -> 'done'
    // Screenshot/automation bypass — see CookieBanner.jsx for the same gate.
    if (new URLSearchParams(window.location.search).has("screenshot")) return null;
    const settled = !!(user && typeof user === "object" && user.id);
    if (!settled) return null;
    return (
        <>
            <ConsentRenewalModal active={stage === "legal"} onSettled={() => setStage("telemetry")} />
            <TelemetryConsentModal active={stage === "telemetry"} onSettled={() => setStage("done")} />
        </>
    );
}

function GlobalOverlays() {
    // Hide the cookie banner on /lite — it's an anonymous frictionless PWA
    // where those interrupts defeat the purpose (Round 27).
    const { pathname } = useLocation();
    if (pathname.startsWith("/lite")) return null;
    return (
        <>
            <ConsentCoordinator />
            <CookieBanner />
            <CookiePreferencesLauncher />
            <GlobalBackButton />
            <FeedbackWidget />
        </>
    );
}


function RtlAwareToaster() {
    // Sonner inherits `dir` from <html>, but position needs to flip
    // to top-left in RTL so it lands on the reader's leading side.
    const { user } = useAuth();
    const isRtl = (user?.preferred_language || "en").toString().toLowerCase().startsWith("ar");
    return <Toaster position={isRtl ? "top-left" : "top-right"} richColors dir={isRtl ? "rtl" : "ltr"} />;
}

export default function App() {
    return (
        <ThemeProvider>
            <DensityProvider>
                <BrowserRouter>
                    <AuthProvider>
                    <CurrencyProvider>
                    <SWRegister />
                    <VersionGuard />
                    <BehavioralTelemetry />
                    <RouteActivityLogger />
                    <RtlDirection />
                    <PostTradeDebriefModal />
                    <GlobalOverlays />
                    <RtlAwareToaster />
                    <Routes>
                        {/* ── Public marketing / auth ─────────────────────────── */}
                        <Route path="/" element={<Landing />} />
                        <Route path="/watch" element={<Watch />} />
                        <Route path="/about" element={<About />} />
                        <Route path="/contact" element={<Contact />} />
                        <Route path="/faq" element={<FAQ />} />
                        <Route path="/legal/:slug" element={<LegalPage />} />
                        <Route path="/login" element={<Login />} />
                        <Route path="/register" element={<Register />} />
                        <Route path="/recover-with-codes" element={<RecoverWithCodes />} />
                        <Route path="/auth/callback" element={<AuthCallback />} />

                        {/* /lite (Obondo Lite) retired 2026-09 — every signup now gets
                            the full 7-day Obondo trial, no anonymous taste needed. */}
                        <Route path="/lite" element={<Navigate to="/register?intent=obondo_trial" replace />} />
                        {/* /kyc route retired Feb 2026 — closed-loop simulator no longer requires KYC.
                            Legacy links auto-redirect users to their dashboard. */}
                        <Route path="/kyc" element={<Navigate to="/app" replace />} />

                        {/* ── Retail / trader area (/app/*) ───────────────────── */}
                        {/* Dashboard root — admin gets bounced to /admin here. */}
                        <Route element={<RouteGuard redirectAdmin><AppLayout /></RouteGuard>}>
                            <Route path="/app" element={<Dashboard />} />
                            <Route path="/app/subscription" element={<Subscription />} />
                        </Route>
                        {/* Trader tools — admin may still browse these read-only. */}
                        <Route element={<RouteGuard><AppLayout /></RouteGuard>}>
                            <Route path="/onboarding/investment-strategy" element={<InvestmentStrategyOnboarding />} />
                            <Route path="/onboarding/psychometric" element={<PsychometricOnboarding />} />
                            {/* ── Rebranded routes (Aug 2026) ──────────────────── */}
                            <Route path="/app/place-a-trade" element={<PlaceATrade />} />
                            <Route path="/app/place-a-trade/instrument/:symbol" element={<InstrumentInspector />} />
                            <Route path="/app/whos-on-top" element={<WhosOnTop />} />
                            <Route path="/app/whos-on-top/trader/:traderId" element={<TraderProfile />} />
                            <Route path="/app/who-is-around" element={<Community />} />
                            <Route path="/app/copilot/kimani" element={<AiAssistant />} />
                            <Route path="/app/copilot/obondo" element={<RBACCoPilotGate requiredTier="paid"><ObondoCoach /></RBACCoPilotGate>} />
                            {/* ── Legacy routes → redirects ────────────────────── */}
                            <Route path="/app/terminal" element={<Navigate to="/app/place-a-trade" replace />} />
                            <Route path="/app/catalog" element={<Navigate to="/app/place-a-trade" replace />} />
                            <Route path="/app/macro" element={<Navigate to="/app/place-a-trade" replace />} />
                            <Route path="/app/leaderboard" element={<Navigate to="/app/whos-on-top" replace />} />
                            <Route path="/app/copy-trading" element={<Navigate to="/app/whos-on-top" replace />} />
                            <Route path="/app/community" element={<Navigate to="/app/who-is-around" replace />} />
                            <Route path="/app/dual-key" element={<Navigate to="/app/place-a-trade" replace />} />
                            <Route path="/app/assistant" element={<Navigate to="/app/copilot/kimani" replace />} />
                            <Route path="/app/coach" element={<Navigate to="/app/copilot/obondo" replace />} />
                            {/* ── Kept as-is ───────────────────────────────────── */}
                            <Route path="/app/macro/:code" element={<CountryDetailPage />} />
                            <Route path="/app/settings" element={<Settings />} />
                            <Route path="/app/options" element={<FeatureRetired module="Options Trading" replacement="live equity trading in Place a Trade" />} />
                            <Route path="/app/shorts" element={<FeatureRetired module="Short Selling" replacement="long-only trading + Copy Trading in Who's On Top" />} />
                            <Route path="/app/bonds" element={<FeatureRetired module="Bonds & Fixed Income" replacement="the Macro dashboard for regional rates" />} />
                            <Route path="/app/backtest" element={<BacktestReport />} />
                            <Route path="/app/portfolio" element={<PortfolioSuggestion />} />
                            <Route path="/app/autopilot/log" element={<AutopilotAuditLog />} />
                            <Route path="/app/obondo-billing" element={<ObondoBillingPage />} />
                            <Route path="/dashboard/baseline-setup" element={<Navigate to="/onboarding/psychometric" replace />} />
                            <Route path="/app/expert" element={<FeatureRetired module="Expert Charts" replacement="the live chart on each instrument's detail page" />} />
                        </Route>

                        {/* ── Super Admin area (/admin/*) ─────────────────────── */}
                        {/* Every /admin/* route shares the same AppLayout/Sidebar shell
                            as the trader area for full UI parity (2026-09-05 directive —
                            AdminLayout/AdminHeader retired). */}
                        <Route element={<RouteGuard adminOnly><AppLayout /></RouteGuard>}>
                            <Route path="/admin" element={<Admin />} />
                            <Route path="/admin/settings" element={<AdminSettings />} />
                            <Route path="/admin/analytics/revenue" element={<AdminRevenue />} />
                            <Route path="/admin/inbox" element={<AdminInbox />} />
                            <Route path="/admin/chats" element={<AdminChats />} />
                            <Route path="/admin/faq-gaps" element={<AdminFaqGaps />} />
                            <Route path="/admin/risk-profiles" element={<AdminRiskProfiles />} />
                            <Route path="/admin/questionnaire-i18n" element={<AdminQuestionnaireI18n />} />
                            <Route path="/admin/paybill" element={<AdminPaybill />} />
                            <Route path="/admin/compliance/layer11-audit" element={<AdminLayer11Audit />} />
                            <Route path="/admin/symbols" element={<AdminSymbols />} />
                        </Route>
                        {/* /admin/compliance retired Feb 2026 — closed-loop launch removed KYC. */}
                        <Route path="/admin/compliance" element={<Navigate to="/admin" replace />} />

                        {/* ── Broker (agency owner) — Institutional tier ──────── */}
                        <Route element={<RouteGuard brokerOnly><AppLayout /></RouteGuard>}>
                            <Route path="/agency" element={<AgencyDashboard />} />
                        </Route>
                        <Route path="/for-brokers" element={<BrokerSignup />} />
                        <Route path="/apply" element={<ApiApplicationPage />} />
                        <Route path="/agency-invite/:token" element={<AgencyInviteAccept />} />

                        <Route path="*" element={<Navigate to="/" replace />} />
                    </Routes>
                </CurrencyProvider>
                </AuthProvider>
                </BrowserRouter>
            </DensityProvider>
        </ThemeProvider>
    );
}
